import {Graphics, Rectangle} from "pixi.js";
import {inches} from "../../const";
import {glyphStyle, GlyphFootprintContext, GlyphRect, hasCategory, HardwareGlyph, hitAreaOf} from "./HardwareGlyph";

// a concealed overhead stop / holder: the track mortised into the head's soffit and the
// channel in the top of the leaf, both hidden with the door closed. Drawn as a plain box
// wherever it shows: on the latch edge inside the head section, on the head stick, and on
// the leaf top. Sizes are typical catalogue values (scene units = mm).
const TRACK_LENGTH = 12 * inches;
const TRACK_HEIGHT = 1 * inches;
// in section: the track's width across the door thickness and its depth into the soffit.
const TRACK_WIDTH = 1.25 * inches;
const TRACK_DEPTH = 1.5 * inches;
const OUTLINE = 0.25 * inches;
const HIT_PAD = 1 * inches;

// the track / channel box centred on the hub.
function footprint(ctx: GlyphFootprintContext): GlyphRect[] {
    const x = ctx.hub.x - TRACK_LENGTH / 2;
    const y = ctx.hub.y - TRACK_HEIGHT / 2;
    return [{x0: x, y0: y, x1: x + TRACK_LENGTH, y1: y + TRACK_HEIGHT}];
}

export const concealedOHStop: HardwareGlyph = {
    id: "concealedOHStop",
    targets: ["door", "frame"],
    matches: item => hasCategory(item, "Concealed OH Stop", "Concealed OH Holder"),
    // face-on: the track (head) or channel (leaf top) as a box centred on the hub.
    draw(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const [track] = footprint(ctx);

        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.2);
        g.drawRect(track.x0, track.y0, TRACK_LENGTH, TRACK_HEIGHT);
        g.endFill();

        return hitAreaOf([track], HIT_PAD);
    },
    footprint,
    // seen from the latch edge: a box inside the head section, up from the soffit, centred
    // on the door thickness. Without a head in the view, a box off the face at the hub.
    drawEdge(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const head = ctx.head;
        const cx = ctx.edge ? (ctx.edge.x0 + ctx.edge.x1) / 2 : head ? (head.x0 + head.x1) / 2 : ctx.face.x;
        const x = cx - TRACK_WIDTH / 2;
        const y = head ? head.soffit - TRACK_DEPTH : ctx.face.y - TRACK_DEPTH / 2;

        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.2);
        g.drawRect(x, y, TRACK_WIDTH, TRACK_DEPTH);
        g.endFill();

        return new Rectangle(x - HIT_PAD, y - HIT_PAD, TRACK_WIDTH + 2 * HIT_PAD, TRACK_DEPTH + 2 * HIT_PAD);
    },
};
